import { useEffect, useMemo, useState } from "react";
import UserMetaCard from "../components/UserProfile/UserMetaCard";
import UserInfoCard from "../components/UserProfile/UserInfoCard";
import UserAddressCard from "../components/UserProfile/UserAddressCard";
import SecurityCard from "../components/UserProfile/SecurityCard";
import StatBand, { type StatItem } from "../components/UserProfile/StatBand";
import PageMeta from "../shared/PageMeta";
import PageBreadcrumb from "../shared/PageBreadCrumb";
import { useAuth } from "../context/AuthContext";
import { profileService } from "../services/profileService";

const MUTED = '#7C8A82';

export default function UserProfiles() {
  const { user, updateUser } = useAuth();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefreshing(true);
      const res = await profileService.getProfile();
      if (!cancelled && res.success && res.data) updateUser(res.data);
      if (!cancelled) setRefreshing(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completion = useMemo(() => {
    if (!user) return 0;
    const fields = [
      user.firstName || user.name,
      user.lastName,
      user.email,
      user.phone || user.mobile,
      user.bio || user.description,
      user.profilePhoto || user.profileImage,
      user.address?.country || user.country,
      user.address?.city || user.city,
      user.address?.state || user.state,
      user.address?.postalCode || user.postalCode || user.zipCode,
      user.address?.street || user.street,
    ];
    const filled = fields.filter((v) => !!v && String(v).trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [user]);

  const stats: StatItem[] = useMemo(() => {
    const departments: string[] =
      Array.isArray(user?.roles) && user!.roles.length > 0
        ? user!.roles.map((r: any) => r.department).filter(Boolean)
        : user?.activeDepartment ? [user.activeDepartment] : [];

    const roleCount =
      Array.isArray(user?.roles) && user!.roles.length > 0
        ? user!.roles.length
        : user?.role ? 1 : 0;

    const status = user?.isActive === false ? "Inactive" : "Active";

    const lastLogin = (() => {
      const raw = user?.lastLoginAt;
      if (!raw) return { value: "—", hint: "No login recorded yet" };
      try {
        const d = new Date(raw);
        return {
          value: d.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
          hint: d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
        };
      } catch { return { value: "—", hint: "No login recorded yet" }; }
    })();

    return [
      {
        label: "Departments",
        value: String(departments.length || 0),
        hint: departments.join(" · ") || "Not assigned",
      },
      {
        label: "Active Roles",
        value: String(roleCount),
        hint: user?.role?.name || (Array.isArray(user?.roles) && user!.roles[0]?.roleId?.name) || "—",
        tone: "gold",
      },
      {
        label: "Account Status",
        value: status,
        hint: user?.isSuperAdmin ? "Super Admin" : user?.activeDepartment ? `Active in ${user.activeDepartment}` : "—",
        tone: status === "Active" ? "positive" : "warning",
        dot: true,
      },
      {
        label: "Last Logged In",
        value: lastLogin.value,
        hint: lastLogin.hint,
      },
    ];
  }, [user]);

  return (
    <>
      <PageMeta
        title="My Profile | Sorigin OMS"
        description="View and update your personal information, address, and account preferences."
      />
      <PageBreadcrumb pageTitle="Profile" />

      <div className="space-y-6 px-5 py-5">
        <UserMetaCard completion={completion} />
        <StatBand stats={stats} />
        <UserInfoCard />
        <UserAddressCard />
        <SecurityCard />

        {refreshing && (
          <p className="text-center text-xs" style={{ color: MUTED }}>
            Refreshing profile…
          </p>
        )}
      </div>
    </>
  );
}
